import type { ReactNode } from "react";

/**
 * The symbols of the Tafo app (Furkan, 08/10/2026: "geen emoji's maar mooie geschikte symbolen"): drawn
 * here in one line style, like the moon and the wheel of the till (ui/icons.tsx), in the colour of the
 * text around them. An emoji looks different on every phone; these look the same on an iPhone, a Samsung
 * and a Sunmi. They say nothing by themselves: the button or line that carries one has the words.
 */
export type AppIconName =
  | "waiter"
  | "courier"
  | "kitchen"
  | "bill"
  | "pay"
  | "cash"
  | "qr"
  | "tap"
  | "card"
  | "terminal"
  | "move"
  | "allergens"
  | "pin"
  | "route"
  | "navigate"
  | "phone"
  | "problem"
  | "check"
  | "note"
  | "offline"
  | "blocked"
  | "document"
  | "computer"
  | "backspace"
  | "settings"
  | "undo"
  | "home"
  | "table"
  | "bag"
  | "chevron";

const line = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const DRAWINGS: Record<AppIconName, ReactNode> = {
  // a cloche on its plate
  waiter: (
    <>
      <path {...line} d="M4.5 16.5a7.5 7.5 0 0 1 15 0Z" />
      <path {...line} d="M12 9V7M10.5 7h3M2.5 19.5h19" />
    </>
  ),
  // a scooter: two wheels, the body over the back wheel, the floor and the steering column
  courier: (
    <>
      <circle {...line} cx="5.5" cy="17.5" r="2.5" />
      <circle {...line} cx="18.5" cy="17.5" r="2.5" />
      <path {...line} d="M8 17.5h7l1-4M16 13.5 14.25 5h-2.5M2.5 15a3.5 3.5 0 0 1 3.5-3.5h5l1.25 3.5" />
    </>
  ),
  // a chef's hat
  kitchen: (
    <path
      {...line}
      d="M7 14.5a4 4 0 0 1-.6-7.95A5.5 5.5 0 0 1 12 3a5.5 5.5 0 0 1 5.6 3.55A4 4 0 0 1 17 14.5V20H7Z M7 17h10"
    />
  ),
  // a receipt with a torn edge
  bill: (
    <>
      <path {...line} d="M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5Z" />
      <path {...line} d="M9 8h6M9 11.5h6M9 15h3.5" />
    </>
  ),
  // a wallet
  pay: (
    <>
      <path {...line} d="M3.5 7.5h15a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2Z" />
      <path {...line} d="M3.5 7.5 15 4l1 3.5M15.5 13.75h2" />
    </>
  ),
  // a banknote
  cash: (
    <>
      <rect {...line} x="2.5" y="6" width="19" height="12" rx="2" />
      <circle {...line} cx="12" cy="12" r="2.5" />
      <path {...line} d="M6 9.5v.01M18 14.5v.01" />
    </>
  ),
  qr: (
    <>
      <rect {...line} x="3.5" y="3.5" width="6.5" height="6.5" rx="1" />
      <rect {...line} x="14" y="3.5" width="6.5" height="6.5" rx="1" />
      <rect {...line} x="3.5" y="14" width="6.5" height="6.5" rx="1" />
      <path {...line} d="M14 14h2.5v2.5M20.5 14v.01M14 20.5h.01M17.25 20.5h3.25v-3.25" />
    </>
  ),
  // contactless: the waves of a card held against a phone
  tap: <path {...line} d="M8.5 9a4.5 4.5 0 0 1 0 6M12 6.5a8 8 0 0 1 0 11M15.5 4a11.5 11.5 0 0 1 0 16M5 11.5v1" />,
  card: (
    <>
      <rect {...line} x="2.5" y="5" width="19" height="14" rx="2" />
      <path {...line} d="M2.5 10h19M6 15h4" />
    </>
  ),
  // a payment terminal with its screen and keys
  terminal: (
    <>
      <rect {...line} x="5.5" y="2.5" width="13" height="19" rx="2.5" />
      <rect {...line} x="8.5" y="5.5" width="7" height="4.5" rx="0.5" />
      <path {...line} d="M9 13.5h.01M12 13.5h.01M15 13.5h.01M9 17h.01M12 17h.01M15 17h.01" />
    </>
  ),
  // two arrows, there and back
  move: <path {...line} d="M4 8h15l-3.5-3.5M20 16H5l3.5 3.5" />,
  // an ear of wheat
  allergens: (
    <path
      {...line}
      d="M12 21V8M12 8c-2-1-2.5-3-2-5 2 .5 2.5 2.5 2 5Zm0 0c2-1 2.5-3 2-5-2 .5-2.5 2.5-2 5Zm0 5c-2.5 0-4-1.5-4.5-4 2.5 0 4 1.5 4.5 4Zm0 0c2.5 0 4-1.5 4.5-4-2.5 0-4 1.5-4.5 4Zm0 5c-2.5 0-4-1.5-4.5-4 2.5 0 4 1.5 4.5 4Zm0 0c2.5 0 4-1.5 4.5-4-2.5 0-4 1.5-4.5 4Z"
    />
  ),
  pin: (
    <>
      <path {...line} d="M12 21.5s-6.5-6-6.5-11.25a6.5 6.5 0 0 1 13 0C18.5 15.5 12 21.5 12 21.5Z" />
      <circle {...line} cx="12" cy="10.25" r="2.4" />
    </>
  ),
  // a winding road from one stop to the next
  route: (
    <>
      <circle {...line} cx="5.5" cy="18.5" r="2" />
      <circle {...line} cx="18.5" cy="5.5" r="2" />
      <path {...line} d="M7.5 18.5h8a3.25 3.25 0 0 0 0-6.5h-7a3.25 3.25 0 0 1 0-6.5h8" />
    </>
  ),
  // the arrow of a navigator
  navigate: <path {...line} d="M3.5 10.5 20.5 3.5l-7 17-2.25-7.75Z" />,
  phone: (
    <path
      {...line}
      d="M6.5 3.5h2.75l1.75 4.5-2.25 1.4a10.5 10.5 0 0 0 5.85 5.85L16 13l4.5 1.75v2.75a2.5 2.5 0 0 1-2.5 2.5A15.5 15.5 0 0 1 4 6a2.5 2.5 0 0 1 2.5-2.5Z"
    />
  ),
  problem: (
    <>
      <path {...line} d="M10.3 4.2a2 2 0 0 1 3.4 0l7.6 13.1a2 2 0 0 1-1.7 3H4.4a2 2 0 0 1-1.7-3Z" />
      <path {...line} d="M12 9.5v4.25M12 17v.01" />
    </>
  ),
  check: <path {...line} d="M4.5 12.5 9.5 17.5 19.5 6.5" />,
  // a pencil
  note: <path {...line} d="M4 20h4.25L19.5 8.75a2.1 2.1 0 0 0-3-3L5.25 17v3M14.5 7.75l3 3" />,
  offline: (
    <>
      <path {...line} d="M2.5 8.5a14 14 0 0 1 19 0M5.5 12a9.5 9.5 0 0 1 13 0M8.75 15.5a5 5 0 0 1 6.5 0M12 19v.01" />
      <path {...line} d="M3.5 3.5l17 17" />
    </>
  ),
  blocked: (
    <>
      <circle {...line} cx="12" cy="12" r="9" />
      <path {...line} d="M5.75 5.75l12.5 12.5" />
    </>
  ),
  document: (
    <>
      <path {...line} d="M6 2.5h8l4.5 4.5v14.5H6Z" />
      <path {...line} d="M14 2.5V7h4.5M9 12h6M9 15.5h6" />
    </>
  ),
  computer: (
    <>
      <rect {...line} x="2.5" y="4" width="19" height="12.5" rx="2" />
      <path {...line} d="M12 16.5v3.5M8 20h8" />
    </>
  ),
  backspace: (
    <>
      <path {...line} d="M9 5h11a1.5 1.5 0 0 1 1.5 1.5v11A1.5 1.5 0 0 1 20 19H9l-6.5-7Z" />
      <path {...line} d="M12.5 9.5l5 5M17.5 9.5l-5 5" />
    </>
  ),
  // the wheel of the till's Beheer (ui/icons.tsx GearIcon)
  settings: (
    <path
      fill="currentColor"
      fillRule="evenodd"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinejoin="round"
      d="M10.27 4.8 10.49 1.91 13.51 1.91 13.73 4.8 15.87 5.69 18.07 3.8 20.2 5.93 18.31 8.13 19.2 10.27 22.09 10.49 22.09 13.51 19.2 13.73 18.31 15.87 20.2 18.07 18.07 20.2 15.87 18.31 13.73 19.2 13.51 22.09 10.49 22.09 10.27 19.2 8.13 18.31 5.93 20.2 3.8 18.07 5.69 15.87 4.8 13.73 1.91 13.51 1.91 10.49 4.8 10.27 5.69 8.13 3.8 5.93 5.93 3.8 8.13 5.69Z M12 8.6a3.4 3.4 0 1 0 0 6.8a3.4 3.4 0 1 0 0 -6.8Z"
    />
  ),
  undo: <path {...line} d="M9 14 4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />,
  home: <path {...line} d="M3.5 11 12 4l8.5 7M6 9v11h4.5v-6h3v6H18V9" />,
  /** a table with its four chairs, seen from above */
  table: <path {...line} d="M6.5 8h11v8h-11ZM9.5 4.5h5M9.5 19.5h5M3 10.5v3M21 10.5v3" />,
  bag: <path {...line} d="M5 8h14l-1.2 12H6.2L5 8ZM9 8V6.5a3 3 0 0 1 6 0V8" />,
  chevron: <path {...line} d="M9 5l7 7-7 7" />,
};

/** One symbol, 20 px by default, in the colour of the text around it. */
export function AppIcon({ name, size = 20 }: { name: AppIconName; size?: number }) {
  return (
    <svg
      className="aicon"
      data-icon={name}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      {DRAWINGS[name]}
    </svg>
  );
}
